import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
import { appConfig } from '@/config/app-config';
export interface Equipment {
  id: number;
  code: string;
  description: string;
  teu: number;
  max_tare: number | null;
  tare: number | null;
  volume: number | null;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Interfaz para opciones de filtro (usado en dropdowns)
export interface EquipmentFilterOption {
  value: string;
  label: string;
  code?: string;
  teu?: number;
  avatar_url?: string;
}
interface EquipmentState extends FilteredState<Equipment> {
  selectedEquipment: Equipment | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  // Estado para opciones de filtro (cacheadas para evitar recargas)
  filterOptions: EquipmentFilterOption[];
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  filterOptionsLastFetch: number | null;
}
const initialState: EquipmentState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'code',
  sortDirection: 'asc',
  searchTerm: null,
  selectedEquipment: null,
  creating: false,
  updating: false,
  deleting: false,
  // Estado para opciones de filtro
  filterOptions: [],
  filterOptionsLoading: false,
  filterOptionsError: null,
  filterOptionsLastFetch: null,
};
// Thunk para cargar equipment con paginación y filtros (para vistas de lista)
export const fetchEquipment = createAsyncThunk<{ data: Equipment[]; totalCount: number }, any>('equipment/fetchEquipment', async (params: any) => {
  throw new Error('Not implemented yet');
});
// Thunk para cargar opciones de filtro (solo id, código, descripción) - CACHEADO
export const fetchEquipmentFilterOptions = createAsyncThunk(
  'equipment/fetchFilterOptions',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const equipmentState = state.equipment as EquipmentState;
    // ✅ CACHE: Si ya tenemos datos frescos (menos de 5 minutos), usar cache
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
    if (
      equipmentState.filterOptionsLastFetch &&
      Date.now() - equipmentState.filterOptionsLastFetch < CACHE_DURATION &&
      equipmentState.filterOptions.length > 0
    ) {
      return equipmentState.filterOptions;
    }
    try {
      // Obtener token de autenticación
      const token = localStorage.getItem('authToken') || '';
      const url = appConfig.getApiUrl('masters/equipment?startRow=0&endRow=500&sortProperty=code&sortDirection=asc');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      const result = await response.json();
      const equipment = result?.data?.rows || [];
      // Transformar a formato de opciones de filtro
      const filterOptions: EquipmentFilterOption[] = equipment.map((equip: any) => ({
        value: equip.id.toString(),
        label: equip.code,
        code: equip.code,
        teu: equip.teu,
        avatar_url: equip.avatar_url
      }));
      return filterOptions;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error al cargar opciones de filtro');
    }
  }
);
export const fetchEquipmentById = createAsyncThunk('equipment/fetchEquipmentById', async (id: number) => {
  throw new Error('Not implemented yet');
});
export const createEquipment = createAsyncThunk('equipment/createEquipment', async (equipment: Partial<Equipment>) => {
  throw new Error('Not implemented yet');
});
export const updateEquipment = createAsyncThunk('equipment/updateEquipment', async ({ id, data }: { id: number; data: Partial<Equipment> }) => {
  throw new Error('Not implemented yet');
});
export const deleteEquipment = createAsyncThunk('equipment/deleteEquipment', async (id: number) => {
  throw new Error('Not implemented yet');
});
const equipmentSlice = createSlice({
  name: 'equipment',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectEquipment: (state, action: PayloadAction<Equipment | null>) => {
      state.selectedEquipment = action.payload;
    },
    clearSelection: (state) => {
      state.selectedEquipment = null;
    },
  },
  extraReducers: (builder) => {
    // Casos para fetchEquipment (lista completa)
    builder
      .addCase(fetchEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEquipment.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar equipos';
      })
      // Casos para fetchEquipmentFilterOptions (opciones cacheadas)
      .addCase(fetchEquipmentFilterOptions.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchEquipmentFilterOptions.fulfilled, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptions = action.payload;
        state.filterOptionsLastFetch = Date.now();
      })
      .addCase(fetchEquipmentFilterOptions.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload as string || 'Error al cargar opciones de filtro';
      });
  },
});
// Selectores para acceder al estado
export const selectEquipmentFilterOptions = (state: any) => state.equipment.filterOptions;
export const selectEquipmentFilterOptionsLoading = (state: any) => state.equipment.filterOptionsLoading;
export const selectEquipmentFilterOptionsError = (state: any) => state.equipment.filterOptionsError;
// Helper para verificar si las opciones de filtro están frescas
export const areEquipmentFilterOptionsFresh = (state: any): boolean => {
  const lastFetch = state.equipment.filterOptionsLastFetch;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  return lastFetch && Date.now() - lastFetch < CACHE_DURATION;
};
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectEquipment, clearSelection } = equipmentSlice.actions;
export default equipmentSlice.reducer;
